import React, { useState } from 'react';
import { Row, Form, Input, notification, TreeSelect } from 'antd';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import Popconfirm from 'components/PopConfirm';

import { AddFolder, FolderIcon, DropdownIcon } from 'components/Icons';
import { CONTENT_LIBRARY_ADD_ITEM } from 'graphql/mutations/contentLibraryMutations';
import { getProposalTitle } from 'helpers/proposal';

import './MoveLibraryModal.scss';

/**
 * Move library modal
 *
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Function} props.onCancel
 * @param {Object} props.template
 * @param {Object} props.catalog
 * @param {string} props.selectedFolderKey
 * @param {string} props.selectedPrelistFolderKey
 * @param {Object} props.treeData
 * @param {Function} props.refetchTemplates
 * @param {Function} props.refetchCatalogs
 * @param {boolean} props.isDashboard
 * @param {boolean} props.isTemplate
 * @param {string} props.isCatalog
 * @param {Array} props.catalogIds
 * @param {Function} props.saveDeliverable
 */
const MoveLibraryModal = ({
  language,
  visible,
  onCancel,
  template,
  catalog,
  selectedFolderKey,
  selectedPrelistFolderKey,
  treeData,
  refetchTemplates,
  refetchCatalogs,
  isDashboard,
  isTemplate,
  isCatalog,
  catalogIds,
  setCheckedCatalogs,
  onKeySelect,
  addNewChildFromSearch,
  handleTreeSearch,
  saveDeliverable,
  initialDeliverableName,
}) => {
  /**
   * Initial item name
   */
  const initialItemName = `${
    isTemplate
      ? `Copy of ${template?.templateName ? template.templateName : template?.project?.name}`
      : isCatalog && isCatalog !== 'Add-Deliverable'
      ? `Copy of ${catalog?.name}`
      : isCatalog === 'Add-Deliverable'
      ? initialDeliverableName
      : `${getProposalTitle({ proposal: template })}-Template`
  }`;

  const [toFolderKey, setToFolderKey] = useState({
    key: selectedFolderKey || '',
    treeId: treeData?._id || '',
  });
  const [moveLibraryError, setMoveLibraryError] = useState('');
  const [loading, setLoading] = useState(false);
  const [localSelectedFolderKey, setLocalSelectedFolderKey] = useState(selectedFolderKey);
  const [submitDisabled, setSubmitDisabled] = useState(initialItemName ? false : true);

  const [form] = Form.useForm();

  /**
   * Add item mutation
   */
  const [addItem] = useMutation(CONTENT_LIBRARY_ADD_ITEM, {
    onCompleted() {
      notification.success({
        description: isDashboard
          ? isTemplate
            ? `Template saved successfully to ${toFolderKey?.name || 'All Templates'}.`
            : 'Template saved successfully.'
          : isCatalog === 'Duplicate' || isCatalog === 'Duplicate-Deliverable'
          ? `Catalog duplicated successfully to ${toFolderKey?.name || 'All Catalogs'}.`
          : isCatalog === 'Add-Deliverable'
          ? `Deliverable added successfully to ${toFolderKey?.name || 'All Deliverables'}.`
          : 'Item moved successfully.',
      });
      onCancel();
      if (refetchTemplates) {
        refetchTemplates();
      }
      if (refetchCatalogs) {
        refetchCatalogs();
      }
      setToFolderKey('');
      form.resetFields();
      setLoading(false);
      setCheckedCatalogs(false);
    },
    onError() {
      setLoading(false);
      setMoveLibraryError(isDashboard ? 'Template save failed.' : 'Item moved failed.');
      setCheckedCatalogs(false);
    },
  });

  /**
   * On select tree item
   *
   * @param {Object} selectedTree
   */
  const onSelectTreeItem = (selectedTree) => {
    if (selectedTree.type === 'new' && selectedTree.name) {
      addNewChildFromSearch(selectedTree.name);
    }

    if (selectedTree.key) setMoveLibraryError('');
    setToFolderKey(selectedTree);
  };

  /**
   * Handle submit
   */
  const handleSubmit = () => {
    if (isDashboard) {
      form
        .validateFields()
        .then((values) => {
          setLoading(true);
          addItem({
            variables: {
              fromKey: selectedFolderKey,
              id: toFolderKey?.treeId || '',
              key: toFolderKey?.key || '',
              itemId: template._id,
              itemIds: catalogIds || '',
              newTemplateName: values.itemName,
              type: isTemplate ? 'template' : 'proposal',
            },
          });
        })
        .catch((info) => {
          console.log('Validate Failed:', info);
        });
    } else {
      // Check if no folder is selected
      if (!localSelectedFolderKey && !selectedFolderKey) {
        // Show error notification if no folder is selected
        return notification.error({
          message: 'Error',
          description: 'Please select a folder to move the item to.',
          duration: 3,
        });
      }

      // Set loading to true and add item
      setLoading(true);

      if (isCatalog === 'Add-Deliverable') {
        if (saveDeliverable) {
          let deliverable = {
            name: form.getFieldValue('itemName'),
            folderKey: toFolderKey?.key,
            price: {
              amount: 0,
              type: '',
              items: 1,
              quantity: 1,
            },
            discount: {
              value: 0,
              unit: '%',
            },
            images: [],
            description: '',
          };
          saveDeliverable(deliverable, () => {
            onCancel();
            setToFolderKey('');
            form.resetFields();
            setLoading(false);
          });
        }
      } else {
        // Add item
        const targetTreeId = toFolderKey?.treeId || treeData?._id || '';
        const targetKey = toFolderKey?.key || selectedFolderKey || '';

        const finalTreeId =
          selectedFolderKey === targetKey && !targetTreeId ? treeData?._id || '' : targetTreeId;

        addItem({
          variables: {
            fromKey: selectedFolderKey,
            id: finalTreeId,
            key: targetKey,
            itemId:
              isCatalog === 'Add-Deliverable' ? '' : isCatalog ? catalog?._id || '' : template?._id,
            itemIds: catalogIds || '',
            newCatalogName: isCatalog === 'Duplicate' ? form.getFieldValue('itemName') : '',
            type:
              isCatalog === 'Duplicate' || isCatalog === 'Duplicate-Deliverable'
                ? 'duplicate-catalog'
                : isCatalog === 'Add-Deliverable'
                ? 'add-deliverable'
                : isCatalog
                ? 'catalog'
                : isTemplate
                ? 'template'
                : 'proposal',
          },
        });
      }
    }
  };

  /**
   * On close move modal
   */
  const onCloseMoveModal = () => {
    onCancel();
    setCheckedCatalogs(false);
    form.resetFields();
    setToFolderKey({});
    setLocalSelectedFolderKey('');
    setSubmitDisabled(initialItemName ? false : true);
    setMoveLibraryError('');
    setLoading(false);
  };

  /**
   * Get title from treeData based on folderKey
   * @param {string} folderKey - The folder key to search for
   * @param {Array} children - The tree children array
   * @returns {string} - The title of the folder
   */
  const getFolderKeyFromTreeData = (folderKey, children = []) => {
    for (const child of children) {
      if (child.path === folderKey) {
        return child.key;
      }
      if (child.children && child.children.length > 0) {
        const foundKey = getFolderKeyFromTreeData(folderKey, child.children);
        if (foundKey) {
          return foundKey;
        }
      }
    }
    return '';
  };

  const languageClass =
    language === 'hebrew' ||
    language === 'arabic' ||
    template?.language === 'hebrew' ||
    template?.language === 'arabic'
      ? 'hebrew'
      : '';

  /**
   * Render
   */
  return (
    <Popconfirm
      modalClassName={`move-to-folder-modal ${languageClass}`}
      title={`${
        isDashboard
          ? 'Save'
          : isCatalog === 'Duplicate' || isCatalog === 'Duplicate-Deliverable'
          ? 'Duplicate'
          : isCatalog === 'Add-Deliverable'
          ? 'New Deliverable'
          : 'Move'
      } ${
        template
          ? 'Template'
          : isCatalog === 'Duplicate-Deliverable'
          ? 'Deliverable'
          : isCatalog === 'Add-Deliverable'
          ? ''
          : isCatalog
          ? 'Product'
          : isCatalog && catalogIds?.length
          ? 'Products'
          : 'Cover'
      }`}
      body={
        <div>
          {(isDashboard ||
            (isCatalog === 'Duplicate' && !catalogIds?.length) ||
            isCatalog === 'Add-Deliverable') && (
            <Row className="move-to-library-input">
              <label>
                {isTemplate || isDashboard
                  ? 'Template'
                  : isCatalog
                  ? 'Deliverable'
                  : isCatalog && catalogIds?.length
                  ? 'Deliverables'
                  : 'Cover'}{' '}
                Name
              </label>
              <Form
                form={form}
                layout="vertical"
                name="form_in_modal"
                initialValues={{
                  modifier: 'public',
                  itemName: initialItemName,
                }}
                onValuesChange={(changedValues) => {
                  if (!changedValues.itemName || changedValues.itemName.trim() === '') {
                    setSubmitDisabled(true);
                  } else {
                    setSubmitDisabled(false);
                  }
                }}>
                <Form.Item
                  name="itemName"
                  rules={[
                    {
                      required: true,
                      message:
                        isCatalog === 'Add-Deliverable'
                          ? 'Deliverable name is required'
                          : 'Template name is required',
                    },
                    { whitespace: true, message: 'Field cannot be only spaces' },
                  ]}>
                  <Input />
                </Form.Item>
              </Form>
            </Row>
          )}

          <Row className="move-to-library-list">
            <label>Select/create folder</label>
            <TreeSelect
              showSearch
              virtual={false}
              treeIcon={true}
              suffixIcon={<DropdownIcon />}
              placement="bottom"
              placeholder="Select/create folder"
              popupClassName="move-library-tree-select-popup"
              dropdownRender={(menu) => (
                <div
                  className={`select-folder-tree-select ${
                    languageClass ? 'hebrew-tree-select' : ''
                  }`}>
                  {menu}
                </div>
              )}
              onSearch={handleTreeSearch}
              treeData={(treeData?.children || [])
                .filter((item) => item.title !== null)
                .map((item) => {
                  return {
                    ...item,
                    icon: item.create ? <AddFolder /> : <FolderIcon />,
                  };
                })}
              defaultValue={getFolderKeyFromTreeData(
                localSelectedFolderKey || selectedFolderKey || selectedPrelistFolderKey,
                treeData?.children || []
              )}
              value={getFolderKeyFromTreeData(localSelectedFolderKey, treeData?.children || [])}
              onChange={(keys, info) => {
                const selectedTree = keys.split('_');
                // Check if clicking same key (already selected), unselect it; else proceed as normal
                if (localSelectedFolderKey === selectedTree[0] && selectedTree?.[3] !== 'new') {
                  onSelectTreeItem({
                    key: '',
                    treeId: treeData._id,
                    name: '',
                    type: '',
                  });
                  onKeySelect([['']], false);
                  // If the same key is selected, unselect it
                  setLocalSelectedFolderKey('');
                  return;
                }

                const [newValue] = info;

                onSelectTreeItem({
                  key: selectedTree?.[0],
                  treeId: treeData._id,
                  name: newValue || '',
                  type: selectedTree?.[3] || '',
                });

                onKeySelect([selectedTree], true);
                setLocalSelectedFolderKey(selectedTree?.[0] || '');
              }}
            />
          </Row>

          <p className="error">{moveLibraryError}</p>
        </div>
      }
      visible={visible}
      cancelText="CANCEL"
      confirmText={
        isDashboard || isCatalog === 'Add-Deliverable'
          ? 'SAVE'
          : isCatalog === 'Duplicate' || isCatalog === 'Duplicate-Deliverable'
          ? 'DUPLICATE'
          : 'MOVE'
      }
      onCancel={onCloseMoveModal}
      onConfirm={handleSubmit}
      okButtonProps={{
        loading,
        disabled: loading || submitDisabled,
      }}
    />
  );
};

/**
 * Default props
 */
MoveLibraryModal.defaultProps = {
  initialDeliverableName: '',
  isTemplate: false,
  visible: false,
  template: '',
  selectedFolderKey: '',
  selectedPrelistFolderKey: '',
  catalog: '',
  refetchTemplates: () => {},
  refetchCatalogs: () => {},
  isDashboard: false,
  catalogIds: [],
  setCheckedCatalogs: () => {},
  onKeySelect: () => {},
  saveDeliverable: () => {},
};

/**
 * Prop types
 */
MoveLibraryModal.propTypes = {
  initialDeliverableName: PropTypes.string,
  visible: PropTypes.bool,
  template: PropTypes.instanceOf(Object),
  catalog: PropTypes.instanceOf(Object),
  onCancel: PropTypes.func.isRequired,
  selectedFolderKey: PropTypes.string,
  selectedPrelistFolderKey: PropTypes.string,
  refetchTemplates: PropTypes.func,
  refetchCatalogs: PropTypes.func,
  isDashboard: PropTypes.bool,
  catalogIds: PropTypes.array,
  setCheckedCatalogs: PropTypes.func,
  onKeySelect: PropTypes.func,
  saveDeliverable: PropTypes.func,
};

export default MoveLibraryModal;
